import { useEffect, useRef } from 'react';
import connectionHubManager from 'connectionHub/connectionHubManager';
import RMHub from 'connectionHub/RMHub';

const NOTIFICATION_HUB_NAME = 'notificationHub';
const NOTIFICATION_HUB_PATH = '/notificationHub';

const isValidExperimentId = (
  experimentId: number | null | undefined,
): experimentId is number => !!experimentId && !Number.isNaN(experimentId);

const joinExperiment = (connection: RMHub, experimentId: number) => {
  connection.invoke('JoinExperiment', experimentId).catch((error) => {
    console.error('Failed to join experiment presence:', error);
  });
};

// Registers this open experiment page with the server (via the shared notification hub
// connection) so the backend can track which experiments are currently open — used today
// only as a check when Archiving is run. The connection is kept in a ref, not state: this
// hook runs directly inside ExperimentDashboard (a large component), and the connection
// resolving is not something the page needs to re-render for.
//
// The connection lifecycle (join on mount, leave on unmount) is deliberately split from the
// re-join-on-change effect below: if a single effect fired both "leave the old experiment"
// and "join the new one" whenever experimentId changes, nothing guarantees those two
// independent SignalR calls land in that order, so a slow Leave could arrive after the new
// Join and wrongly evict it. Switching experiments while this stays mounted should only ever
// upsert (Join), never Leave; Leave fires exactly once, when this unmounts for good, which by
// then can't race a Join since no more will be sent.
export const useExperimentPresence = (experimentId: number | null | undefined) => {
  const connectionRef = useRef<RMHub | null>(null);
  const experimentIdRef = useRef(experimentId);
  experimentIdRef.current = experimentId;

  useEffect(() => {
    let cancelled = false;
    let reconnectSubscription: { dispose: () => void } | null = null;

    const init = async () => {
      try {
        const connection = await connectionHubManager.getConnection(
          NOTIFICATION_HUB_NAME,
          NOTIFICATION_HUB_PATH,
        );
        if (cancelled) {
          return;
        }
        connectionRef.current = connection;

        // Join whatever experiment is current by the time the connection is ready — it may
        // have changed (or arrived) while this was still connecting.
        if (isValidExperimentId(experimentIdRef.current)) {
          joinExperiment(connection, experimentIdRef.current);
        }

        reconnectSubscription = connection.reconnected.add(() => {
          if (isValidExperimentId(experimentIdRef.current)) {
            joinExperiment(connection, experimentIdRef.current);
          }
        });
      } catch (error) {
        console.error('Failed to initialize experiment presence:', error);
      }
    };

    void init();

    return () => {
      cancelled = true;
      reconnectSubscription?.dispose();
      connectionRef.current?.invoke('LeaveExperiment').catch((error) => {
        console.error('Failed to leave experiment presence:', error);
      });
    };
  }, []);

  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || !isValidExperimentId(experimentId)) {
      return;
    }
    joinExperiment(connection, experimentId);
  }, [experimentId]);
};

export default useExperimentPresence;
